import { Banknote, Monitor } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { SavingsAccount } from '@/api/savings-types'
import type { DualControlResult } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useIsAtLeast } from '@/lib/use-viewport'

import { SavingsDualControlNotice, SavingsDualControlOutcome } from './dual-control'
import { useCloseSavingsAccount, useReactivateSavingsAccount } from './mutations'
import { SAVINGS_PERMISSIONS, useSavingsPermissions } from './permissions'
import { SavingsActionDialog } from './savings-action-dialog'

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Bringing a dormant account back into use.
 *
 * No money moves, no till is involved, and the whole act is a stated reason — so it is one of the
 * few savings commands that is entirely safe on a phone. It is refused with
 * `savings.account_not_dormant` on an account that is not dormant, which a screen left open while
 * somebody else acted on the account can walk straight into.
 */
export function ReactivateAccountDialog({
  account,
  open,
  onOpenChange,
}: {
  account: SavingsAccount
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const reactivate = useReactivateSavingsAccount(account.id)

  useEffect(() => {
    if (!open) {
      setReason('')
      reactivate.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const trimmed = reason.trim()

  return (
    <SavingsActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Reactivate the account"
      description="A dormant account cannot be debited until it is brought back into use."
      confirmLabel="Reactivate"
      isPending={reactivate.isPending}
      disabled={trimmed === ''}
      error={reactivate.isError ? reactivate.error : null}
      onConfirm={() => {
        reactivate.mutate(
          { reason: trimmed },
          {
            onSuccess: () => {
              onOpenChange(false)
            },
          },
        )
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="reactivate-reason">Why is it being reactivated?</Label>
        <Input
          id="reactivate-reason"
          value={reason}
          autoComplete="off"
          autoFocus
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
          aria-describedby="reactivate-reason-help"
        />
        <p id="reactivate-reason-help" className="text-xs text-muted">
          Usually that the customer has presented themselves at the counter. Recorded in the audit
          trail.
        </p>
      </div>
    </SavingsActionDialog>
  )
}

/**
 * Closing an account.
 *
 * Two things make this the most consequential action on the screen, and both are stated before the
 * click rather than after it.
 *
 * It is **dual-controlled**, so the answer is a `DualControlResult` and a queued closure has
 * changed nothing.
 *
 * It **pays the balance out in cash**. The handler refunds through the till, `CashDirection.Out`,
 * before the status changes — so it needs an open drawer with the notes in it, and on a security
 * deposit account this is the path by which collateral is handed back over a counter. That is
 * money leaving the institution, which is why {@link CloseAccountAction} refuses it below `lg`
 * rather than offering it on a phone.
 */
export function CloseAccountDialog({
  account,
  open,
  onOpenChange,
}: {
  account: SavingsAccount
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useSavingsPermissions()
  const [reason, setReason] = useState('')
  const [valueDate, setValueDate] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const close = useCloseSavingsAccount(account.id)

  useEffect(() => {
    if (!open) {
      setReason('')
      setValueDate('')
      setResult(null)
      close.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const trimmed = reason.trim()
  const activeHolds = account.holds.filter((hold) => hold.isActive).length

  return (
    <SavingsActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Close the account"
      description="The balance is refunded and the account is settled. It does not reopen."
      notice={
        <div className="space-y-2">
          <SavingsDualControlNotice
            action="Closing an account"
            approvePermission={SAVINGS_PERMISSIONS.closeApprove}
            canCountersign={permissions.canCountersignClose}
          />
          <Alert tone="warning">
            <Banknote aria-hidden />
            <div className="space-y-1">
              <AlertTitle>The refund is paid out in cash</AlertTitle>
              <AlertDescription>
                <MoneyDisplay
                  value={account.ledgerBalanceMinor}
                  currency={account.currencyCode}
                  className="inline text-left"
                />{' '}
                leaves through your till before the status changes, so it needs an open drawer
                holding the notes —{' '}
                <span className="font-mono text-xs">teller.no_open_till</span> and{' '}
                <span className="font-mono text-xs">teller.till_insufficient_cash</span> both refuse
                it.
                {!account.isWithdrawable
                  ? ' On a security deposit this is the path by which collateral is handed back. Be certain the guarantee it secured is discharged.'
                  : ''}
              </AlertDescription>
            </div>
          </Alert>
          {activeHolds > 0 ? (
            <Alert tone="danger">
              <Banknote aria-hidden />
              <div className="space-y-1">
                <AlertTitle>
                  {activeHolds} hold{activeHolds === 1 ? '' : 's'} still active
                </AlertTitle>
                <AlertDescription>
                  The server refuses a closure while anything is earmarked —{' '}
                  <span className="font-mono text-xs">savings.account_has_active_holds</span>.
                  Release them from the Holds tab first.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </div>
      }
      outcome={
        result ? (
          <SavingsDualControlOutcome
            result={result}
            awaitingDescription={`Closing ${account.accountNumber} is waiting for a countersignature. The account is still open, the balance is still the customer's, and no cash has left the drawer.`}
            appliedDescription={`${account.accountNumber} is closed and the balance has been refunded in cash.`}
          />
        ) : null
      }
      confirmLabel="Close account"
      tone="danger"
      isPending={close.isPending}
      disabled={trimmed === ''}
      error={close.isError ? close.error : null}
      onConfirm={() => {
        close.mutate(
          { reason: trimmed, valueDate: valueDate === '' ? null : valueDate },
          { onSuccess: setResult },
        )
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="close-reason">Why is it being closed?</Label>
        <Input
          id="close-reason"
          value={reason}
          autoComplete="off"
          autoFocus
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="close-value-date">Value date (optional)</Label>
        <Input
          id="close-value-date"
          type="date"
          value={valueDate}
          max={today()}
          onChange={(event) => setValueDate(event.target.value)}
          aria-describedby="close-value-date-help"
        />
        <p id="close-value-date-help" className="text-xs text-muted">
          Left empty, the refund takes the branch&apos;s current business date. A backdated date has
          to fall inside the institution&apos;s window.
        </p>
      </div>
    </SavingsActionDialog>
  )
}

/**
 * The close control, with the phone refusal in it.
 *
 * `docs/mobile-policy.md` names this case twice over — "money leaving the institution" and
 * "someone posting a deposit from a phone is not standing where the money is". Closing an account
 * is both: a cash payout at a counter. So below `lg` the button is not offered at all and the
 * reason is stated, rather than being offered and failing at the drawer.
 */
export function CloseAccountAction({ onClose }: { onClose: () => void }) {
  const isWide = useIsAtLeast('lg')

  if (!isWide) {
    return (
      <p className="flex items-start gap-2 text-xs text-muted">
        <Monitor className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        Closing an account pays its whole balance out in cash over the counter. It is available on a
        desktop, at the till.
      </p>
    )
  }

  return (
    <button
      type="button"
      onClick={onClose}
      className="inline-flex h-9 items-center justify-center rounded-control border border-danger-border px-4 text-sm font-medium text-danger-text transition-colors hover:bg-danger-subtle"
    >
      Close account
    </button>
  )
}
